import { put, call, takeEvery, select } from 'redux-saga/effects';
import { replace, push } from 'connected-react-router';
import { UserRole } from '@upstart/harmony/lib/constants';
import { getDealerDiff } from 'components/DealerEditor/DealerEditor';
import { getDealer } from './selectors';
import {
  DEALERS_REQUEST,
  DEALER_REQUEST,
  DEALER_STATUS_REQUEST,
  CREATE_DEALER_REQUEST,
  UPDATE_DEALER_REQUEST,
  DELETE_DEALER_REQUEST,
  MODIFY_DEALER_TMP_STATE_REQUEST,
  INVENTORY_IMPORT_REQUEST,
  DEALER_YMMT_REQUEST,
  DEALER_PRICE_RULES_UPDATE_REQUEST,
  DEALER_PRICE_RULES_FETCH_REQUEST,
  SET_DEALER_THEME_REQUEST,
} from './actionTypes';
import {
  dealersSuccess,
  dealersFailure,
  dealerSuccess,
  dealerFailure,
  dealerStatusSuccess,
  dealerStatusFailure,
  createDealerSuccess,
  createDealerFailure,
  updateDealerSuccess,
  updateDealerFailure,
  deleteDealerSuccess,
  deleteDealerFailure,
  modifyDealerTmpStateSuccess,
  modifyDealerTmpStateFailure,
  inventoryImportSuccess,
  inventoryImportFailure,
  dealerYMMTSuccess,
  dealerYMMTFailure,
  dealerPriceRulesUpdateSuccess,
  dealerPriceRulesUpdateFailure,
  dealerPriceRulesFetchFailure,
  dealerPriceRulesFetchSuccess,
  setDealerThemeSuccess,
  setDealerThemeFailure,
} from './actions';
import { getCurrentUser } from '../user/sagas';
import DealerService from '../../services/DealerService';
import DealerPriceRulesService from '../../services/DealerPriceRulesService';

export function* getDealersTask(action) {
  try {
    const { addExtraInformation } = action.payload || {};
    let list;
    if (addExtraInformation !== undefined) {
      // There's some parameterizing by supplying addExtraInformation boolean, used in the new optimized dealers/all/lightweight route only
      // Use new optimized route
      list = yield call(DealerService.getDealersLightweight, {
        addExtraInformation,
        ...action.payload,
      });
    } else {
      // Maintain backwards compatibility for now because of hotfix
      // Use old unoptimized route
      list = yield call(DealerService.getDealers);
    }
    yield put(dealersSuccess(list));
  } catch (error) {
    yield put(dealersFailure(error));
  }
}

export function* getDealerTask(action) {
  try {
    const { dealerId, editDealer } = action.payload;
    const dealer = yield call(DealerService.getDealer, dealerId);
    if (editDealer) {
      yield put(push(`/dealer/${dealer.id}`));
    }
    yield put(dealerSuccess(dealer));
  } catch (error) {
    yield put(dealerFailure(error));
  }
}

export function* getDealerStatusTask(action) {
  try {
    const { dealerId } = action.payload;
    const dealer = yield call(DealerService.getDealerStatus, dealerId);
    yield put(dealerStatusSuccess(dealer));
  } catch (error) {
    yield put(dealerStatusFailure(error));
  }
}

export function* createDealerTask(action) {
  try {
    const params = action.payload;
    const dealer = yield call(DealerService.createDealer, params);
    yield put(createDealerSuccess(dealer));
    yield put(replace(`/dealer/${dealer.id}`));
  } catch (error) {
    yield put(createDealerFailure(error));
  }
}

function* updateRoute({
  user,
  step,
  maxSteps,
  nextStepOnSuccess,
  route,
  redirectToEditorList,
}) {
  if (nextStepOnSuccess && step < maxSteps) {
    yield put(push(`${route}/${step + 1}`));
  } else if (route && route.indexOf('protection-plans') !== 0) {
    yield put(push(route));
  } else if (redirectToEditorList) {
    if (user.roles.indexOf(UserRole.DEALER_ADMIN) > -1) {
      yield put(push(`/dealer/${user.dealer_id}`));
    } else {
      yield put(push('/dealers'));
    }
  }
}

export function* updateDealerTask(action) {
  try {
    const {
      dealer,
      isDiffObject = false,
      diffDealerBeforeSend = true,
    } = action.payload;

    const shouldDiffDealer = !isDiffObject && diffDealerBeforeSend;
    const dealerUpdatePayload = shouldDiffDealer
      ? {
          ...getDealerDiff(yield select(getDealer), dealer),
          id: dealer.id,
        }
      : dealer;

    const updatedDealer = yield call(
      DealerService.updateDealer,
      dealerUpdatePayload,
      isDiffObject || diffDealerBeforeSend,
    );
    const user = yield call(getCurrentUser);
    yield put(updateDealerSuccess(updatedDealer));
    yield call(updateRoute, { ...action.payload, user });
  } catch (error) {
    yield put(updateDealerFailure(error));
  }
}

export function* updateDealerPriceRules(action) {
  try {
    const { dealerPriceRules } = action.payload;
    const user = yield call(getCurrentUser);
    yield call(DealerPriceRulesService.update, dealerPriceRules);
    yield call(updateRoute, { ...action.payload, user });
    yield put(dealerPriceRulesUpdateSuccess());
  } catch (error) {
    yield put(dealerPriceRulesUpdateFailure(error));
  }
}

export function* fetchDealerPriceRules(action) {
  try {
    const dealerPriceRules = yield call(
      DealerPriceRulesService.get,
      action.payload,
    );
    yield put(dealerPriceRulesFetchSuccess(dealerPriceRules));
  } catch (error) {
    yield put(dealerPriceRulesFetchFailure(error));
  }
}

export function* deleteDealerTask(action) {
  try {
    const dealerId = action.payload;
    yield call(DealerService.deleteDealer, dealerId);
    yield put(deleteDealerSuccess(dealerId));
  } catch (error) {
    yield put(deleteDealerFailure(error));
  }
}

export function* modifyDealerTmpState(action) {
  try {
    const tmpDealer = action.payload;
    yield put(modifyDealerTmpStateSuccess(tmpDealer));
  } catch (error) {
    yield put(modifyDealerTmpStateFailure(error));
  }
}

export function* inventoryImportTask(action) {
  try {
    const dealerId = action.payload;
    yield call(DealerService.inventoryImport, dealerId);
    yield put(inventoryImportSuccess(dealerId));
  } catch (error) {
    yield put(inventoryImportFailure(error));
  }
}

export function* getDealerYMMTTask(action) {
  try {
    const dealerId = action.payload;
    const ymmt = yield call(DealerService.getYMMT, dealerId);
    yield put(dealerYMMTSuccess(ymmt));
  } catch (error) {
    yield put(dealerYMMTFailure(error));
  }
}

export function* setDealerThemeTask(action) {
  try {
    const { dealer, theme } = action.payload;
    const updatedDealer = yield call(
      DealerService.setDealerTheme,
      dealer,
      theme,
    );
    yield put(setDealerThemeSuccess(updatedDealer));
  } catch (error) {
    yield put(setDealerThemeFailure(error));
  }
}

export function* dealersSaga() {
  yield takeEvery(DEALERS_REQUEST, getDealersTask);
  yield takeEvery(DEALER_REQUEST, getDealerTask);
  yield takeEvery(DEALER_STATUS_REQUEST, getDealerStatusTask);
  yield takeEvery(CREATE_DEALER_REQUEST, createDealerTask);
  yield takeEvery(UPDATE_DEALER_REQUEST, updateDealerTask);
  yield takeEvery(DELETE_DEALER_REQUEST, deleteDealerTask);
  yield takeEvery(MODIFY_DEALER_TMP_STATE_REQUEST, modifyDealerTmpState);
  yield takeEvery(INVENTORY_IMPORT_REQUEST, inventoryImportTask);
  yield takeEvery(DEALER_YMMT_REQUEST, getDealerYMMTTask);
  yield takeEvery(DEALER_PRICE_RULES_UPDATE_REQUEST, updateDealerPriceRules);
  yield takeEvery(DEALER_PRICE_RULES_FETCH_REQUEST, fetchDealerPriceRules);
  yield takeEvery(SET_DEALER_THEME_REQUEST, setDealerThemeTask);
}
